import React, { useState } from "react";
import { Box, Skeleton, Typography, styled } from "@mui/material";
import type { RelatedHat } from "../../types";
import { HatViewMoreDialog } from "./HatViewMoreDialog";

interface RelatedHatsProps {
  hats: RelatedHat[];
  onHatClick?: (hatId: string) => void;
  imagesLoading?: boolean;
}

const Container = styled(Box, {
  label: "RelatedHatsContainer",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  width: 176,
}));

const Title = styled(Typography, {
  label: "RelatedHatsTitle",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
  padding: "0 8px",
  whiteSpace: "nowrap",
}));

const HatItem = styled(Box, {
  label: "RelatedHatItem",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  width: "100%",
  cursor: "pointer",
}));

const HatImage = styled("img", {
  label: "RelatedHatImage",
})(() => ({
  width: 64,
  height: 64,
  borderRadius: 16,
  objectFit: "cover",
  flexShrink: 0,
}));

const HatName = styled(Typography, {
  label: "RelatedHatName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  color: "#ffffff",
  lineHeight: "22px",
  flex: 1,
}));

const ViewMore = styled(Typography, {
  label: "RelatedHatsViewMore",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  color: "#ffffff",
  lineHeight: "18px",
  cursor: "pointer",
}));

export const RelatedHats = ({
  hats,
  onHatClick,
  imagesLoading,
}: RelatedHatsProps) => {
  const [viewMoreOpen, setViewMoreOpen] = useState(false);

  if (!hats || hats.length === 0) return null;

  return (
    <Container>
      <Title>{hats.length === 1 ? "Hat" : "Hats"} you might also like</Title>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
        }}
      >
        {hats.slice(0, 2).map((hat) => {
          const imgUrl = hat.hatCard?.smallSquare;
          return (
            <HatItem key={hat.id} onClick={() => onHatClick?.(hat.id)}>
              {imgUrl ? (
                <HatImage src={imgUrl} alt={hat.displayName} />
              ) : imagesLoading ? (
                <Skeleton
                  variant="rounded"
                  width={64}
                  height={64}
                  sx={{
                    flexShrink: 0,
                    borderRadius: "16px",
                    bgcolor: "rgba(255, 255, 255, 0.08)",
                  }}
                />
              ) : null}
              <HatName>{hat.displayName}</HatName>
            </HatItem>
          );
        })}
      </Box>
      {hats.length > 2 && (
        <ViewMore onClick={() => setViewMoreOpen(true)}>View more</ViewMore>
      )}
      <HatViewMoreDialog
        open={viewMoreOpen}
        onClose={() => setViewMoreOpen(false)}
        hats={hats}
        onHatClick={onHatClick}
        imagesLoading={imagesLoading}
      />
    </Container>
  );
};
